import {createPortal} from 'react-dom'
import {useEffect, useRef, useState, type RefObject} from 'react'
import styles from './FlexSuiteHero.module.css'
import {CopilotHead, useWebGLContext} from '@github-ui/brand-webgl'

export const HeroCopilotHead = ({containerRef}: {containerRef: RefObject<HTMLDivElement | null>}) => {
  const {artwork} = useWebGLContext()
  const copilotHeadRef = useRef<HTMLDivElement>(null)
  const [containerEl, setContainerEl] = useState<HTMLDivElement | null>(null)

  useEffect(() => {
    if (containerRef.current !== containerEl) {
      setContainerEl(containerRef.current)
    }
  }, [containerEl, containerRef])

  useEffect(() => {
    if (!artwork || !copilotHeadRef.current) return

    const copilotHead = new CopilotHead(copilotHeadRef.current, artwork.windowSize)

    artwork.addWebGLObject({
      group: copilotHead.group,
      onResize: copilotHead.resize.bind(copilotHead),
      onUpdate: (delta: number, deltaTime: number) => {
        copilotHead.update(delta, deltaTime)
      },
    })

    const onMousemove = (event: MouseEvent) => {
      copilotHead.onMousemove(event)
    }
    window.addEventListener('mousemove', onMousemove)

    const onTouchstart = (event: TouchEvent) => {
      copilotHead.onTouchstart(event)
    }
    window.addEventListener('touchstart', onTouchstart, {passive: true})

    return () => {
      window.removeEventListener('mousemove', onMousemove)
      window.removeEventListener('touchstart', onTouchstart)

      if (copilotHead.blinkTimer) {
        clearTimeout(copilotHead.blinkTimer)
      }
    }
  }, [artwork])

  if (!containerEl) return null

  return createPortal(
    <div className={styles.heroCopilotHeadContainer} aria-hidden="true">
      <div className={styles.heroCopilotHead} data-testid="hero-copilot-head" ref={copilotHeadRef} />
    </div>,
    containerEl,
  )
}
